/**
 * Короткая строка сверки (SAS) голосового звонка — четыре эмодзи из 64.
 *
 * Набор взят из спецификации Matrix (SAS verification, `sas-emoji-v1`): картинки
 * старые, есть во всех системных шрифтах и различимы с первого взгляда, у каждой
 * короткое русское имя — его удобно произнести собеседнику вслух. Порядок и состав
 * менять нельзя: индекс эмодзи — часть протокола, иначе у сторон разойдётся сверка.
 */

export interface SasEmoji {
  emoji: string
  name: string
}

export const SAS_EMOJI: readonly SasEmoji[] = [
  { emoji: '🐶', name: 'Собака' },
  { emoji: '🐱', name: 'Кошка' },
  { emoji: '🦁', name: 'Лев' },
  { emoji: '🐎', name: 'Лошадь' },
  { emoji: '🦄', name: 'Единорог' },
  { emoji: '🐷', name: 'Свинья' },
  { emoji: '🐘', name: 'Слон' },
  { emoji: '🐰', name: 'Кролик' },
  { emoji: '🐼', name: 'Панда' },
  { emoji: '🐓', name: 'Петух' },
  { emoji: '🐧', name: 'Пингвин' },
  { emoji: '🐢', name: 'Черепаха' },
  { emoji: '🐟', name: 'Рыба' },
  { emoji: '🐙', name: 'Осьминог' },
  { emoji: '🦋', name: 'Бабочка' },
  { emoji: '🌷', name: 'Тюльпан' },
  { emoji: '🌳', name: 'Дерево' },
  { emoji: '🌵', name: 'Кактус' },
  { emoji: '🍄', name: 'Гриб' },
  { emoji: '🌏', name: 'Глобус' },
  { emoji: '🌙', name: 'Луна' },
  { emoji: '☁️', name: 'Облако' },
  { emoji: '🔥', name: 'Огонь' },
  { emoji: '🍌', name: 'Банан' },
  { emoji: '🍎', name: 'Яблоко' },
  { emoji: '🍓', name: 'Клубника' },
  { emoji: '🌽', name: 'Кукуруза' },
  { emoji: '🍕', name: 'Пицца' },
  { emoji: '🎂', name: 'Торт' },
  { emoji: '❤️', name: 'Сердце' },
  { emoji: '😀', name: 'Улыбка' },
  { emoji: '🤖', name: 'Робот' },
  { emoji: '🎩', name: 'Шляпа' },
  { emoji: '👓', name: 'Очки' },
  { emoji: '🔧', name: 'Гаечный ключ' },
  { emoji: '🎅', name: 'Санта-Клаус' },
  { emoji: '👍', name: 'Палец вверх' },
  { emoji: '☂️', name: 'Зонт' },
  { emoji: '⌛', name: 'Песочные часы' },
  { emoji: '⏰', name: 'Будильник' },
  { emoji: '🎁', name: 'Подарок' },
  { emoji: '💡', name: 'Лампочка' },
  { emoji: '📕', name: 'Книга' },
  { emoji: '✏️', name: 'Карандаш' },
  { emoji: '📎', name: 'Скрепка' },
  { emoji: '✂️', name: 'Ножницы' },
  { emoji: '🔒', name: 'Замок' },
  { emoji: '🔑', name: 'Ключ' },
  { emoji: '🔨', name: 'Молоток' },
  { emoji: '☎️', name: 'Телефон' },
  { emoji: '🏁', name: 'Флаг' },
  { emoji: '🚂', name: 'Поезд' },
  { emoji: '🚲', name: 'Велосипед' },
  { emoji: '✈️', name: 'Самолёт' },
  { emoji: '🚀', name: 'Ракета' },
  { emoji: '🏆', name: 'Кубок' },
  { emoji: '⚽', name: 'Мяч' },
  { emoji: '🎸', name: 'Гитара' },
  { emoji: '🎺', name: 'Труба' },
  { emoji: '🔔', name: 'Колокольчик' },
  { emoji: '⚓', name: 'Якорь' },
  { emoji: '🎧', name: 'Наушники' },
  { emoji: '📁', name: 'Папка' },
  { emoji: '📌', name: 'Булавка' },
]

/** Сколько эмодзи показывает звонок: 4 × 6 бит = 24 бита сверки, как у Telegram — четыре картинки. */
export const SAS_LENGTH = 4

/**
 * Индексы эмодзи из первых трёх байт строки сверки: 24 бита режутся на четыре
 * шестибитных числа старшими битами вперёд.
 */
export function sasIndices(bytes: Uint8Array): number[] {
  if (bytes.length < 3) throw new RangeError('sas: need 3 bytes')
  const bits = ((bytes[0] ?? 0) << 16) | ((bytes[1] ?? 0) << 8) | (bytes[2] ?? 0)
  const out: number[] = []
  for (let i = SAS_LENGTH - 1; i >= 0; i -= 1) out.push((bits >> (i * 6)) & 0x3f)
  return out
}

export function sasEmoji(indices: readonly number[]): SasEmoji[] {
  return indices.map((index) => {
    const item = SAS_EMOJI[index]
    if (!item) throw new RangeError(`sas: index ${index}`)
    return item
  })
}
